import React from 'react';
import Button from 'components/Button';
import Table from 'components/Table';
import { numberToCurrencyString, dateToLabel } from 'further-ui/utils';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import useAddFeesAndDiscounts, {
  InvestmentWithFee,
  InvestorFee,
} from 'hooks/data/investment/useAddFeesAndDiscounts';
import { FEE_DISCOUNT_SUCCESS } from 'adminConstants/routes';
import { useNotification } from 'hooks/ui/useNotification';
import { type as feeConst } from 'adminConstants/typeConstant';
import ButtonGroup from 'components/ButtonGroup';
import Heading from 'components/Heading';
import { FeeChargeType } from 'further-types/fees';

type Props = RouteComponentProps & {
  investmentFees: Array<InvestmentWithFee>;
  investorFees: Array<InvestorFee>;
  chargeTo: FeeChargeType;
  onGoBack: () => void;
  firmId: string;
};

const investmentColumns = [
  {
    label: 'Fee name',
    key: 'feeName',
    render: (rec: InvestmentWithFee) => rec.newFee.feeName,
  },
  {
    label: 'Name',
    key: 'name',
    render: (rec: InvestmentWithFee) =>
      `${rec.investorId.fullName}${
        rec.externalInvestmentId ? ` (${rec.externalInvestmentId})` : ''
      }`,
  },
  {
    label: 'Subscription date',
    key: 'investmentDate',
    render: (rec: InvestmentWithFee) => {
      return dateToLabel(rec.investmentDate);
    },
  },
  {
    label: 'Original subscription',
    key: 'investmentAmount',
    render: (rec: InvestmentWithFee) => {
      return numberToCurrencyString(rec.investmentAmount);
    },
  },
  {
    label: 'Fee/Discount',
    key: 'feeCalculationBasis',
    render: (rec: InvestmentWithFee) => {
      return rec.newFee.feeCalculationBasis === 'PERCENTAGE'
        ? `${rec.newFee.netFeePercentage}%`
        : numberToCurrencyString(rec?.newFee?.feeExVAT);
    },
  },
  {
    label: 'Fee adjustment',
    key: 'feeAdjustment',
    render: (rec: InvestmentWithFee) => {
      return `${
        rec.newFee.feeType === feeConst.feeTypes.discount ? '-' : ''
      }${numberToCurrencyString(rec?.newFee?.feeIncVAT)}`;
    },
  },
  {
    label: 'VAT',
    key: 'vat',
    render: (rec: InvestmentWithFee) => {
      return rec.newFee.vat ? `${rec.newFee.vatFee}% VAT` : 'No VAT';
    },
  },
];

const investorColumns = [
  {
    label: 'Fee / Discount name',
    key: 'notes',
    render: (rec: InvestorFee) => rec.cashMovement.notes,
  },
  {
    label: 'Investor',
    key: 'fullName',
    render: (rec: InvestorFee) => rec.investorId.fullName,
  },
  {
    label: 'Fee / Discount',
    key: 'amount',
    render: (rec: InvestorFee) => {
      const source = rec.cashMovement.source;
      const amount = numberToCurrencyString(Math.abs(rec.cashMovement.amount));
      return source === 'INVESTOR_DISCOUNT' ? `-${amount}` : amount;
    },
  },
];

const Summary: React.FC<Props> = ({
  investmentFees,
  investorFees,
  onGoBack,
  chargeTo,
  firmId,
}) => {
  const history = useHistory();
  const {
    createInvestmentFee,
    isCreatingFee,
    createInvestorFees,
    isCreatingInvestorFees,
  } = useAddFeesAndDiscounts();
  const { error } = useNotification();

  const handleSubmit = async () => {
    try {
      const response =
        chargeTo === FeeChargeType.Investor
          ? await createInvestorFees({
              firmId,
              investorFees: investorFees.map(({ cashMovement }) => ({
                cashMovement,
              })),
            })
          : await createInvestmentFee({
              firmId,
              investmentFees: investmentFees.map(
                ({ investmentId, newFee }) => ({
                  investmentId,
                  fee: newFee,
                }),
              ),
            });

      if (response.statusCode === 200) {
        history.push(
          `${FEE_DISCOUNT_SUCCESS}?investmentCount=${
            (chargeTo === 'investor' ? investorFees : investmentFees).length
          }`,
        );
      }
    } catch (_) {
      error('Error when adding fees/discounts.');
    }
  };

  return (
    <>
      <Heading variant="h3" noMargin>
        Fee adjustments to be added
      </Heading>
      {chargeTo === FeeChargeType.Investor ? (
        <Table
          variant="nohover"
          columns={investorColumns}
          tablebody={investorFees}
        />
      ) : (
        <Table
          variant="nohover"
          columns={investmentColumns}
          tablebody={investmentFees}
        />
      )}
      <ButtonGroup>
        <Button variant="outlined" onClick={onGoBack}>
          Back
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          loading={isCreatingFee || isCreatingInvestorFees}
        >
          Submit
        </Button>
      </ButtonGroup>
    </>
  );
};

export default Summary;
